import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Card,
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@agion/ui'
import { Sliders, Plus, Trash2 } from 'lucide-react'
import type { CloudEnvironment } from '@/core/infra_types'
import {
  readHelmOverrides,
  writeHelmOverrides,
  findDuplicateKey,
  type HelmOverrideEntry,
  type DroppedEntry,
} from './helm_overrides_merge'
import { getEnvironment, updateEnvironmentConfig } from './infra_environment.service'
import { ApiError } from '@/core/infra_api_client'

interface Props {
  environment: CloudEnvironment
  onUpdated: () => void
}

type Mode = 'viewing' | 'editing' | 'saving'

function entriesEqual(a: HelmOverrideEntry[], b: HelmOverrideEntry[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    const ai = a[i]!
    const bi = b[i]!
    if (ai.key !== bi.key || ai.value !== bi.value || ai.type !== bi.type) {
      return false
    }
  }
  return true
}

export function InfraHelmOverridesCard({ environment, onUpdated }: Props) {
  const { t } = useTranslation(['pages'])

  const readResult = useMemo(
    () => readHelmOverrides((environment.config ?? {}) as Record<string, unknown>),
    [environment]
  )
  const initialEntries = readResult.entries
  const dropped: DroppedEntry[] = readResult.dropped

  const [mode, setMode] = useState<Mode>('viewing')
  const [draft, setDraft] = useState<HelmOverrideEntry[]>(initialEntries)
  const [error, setError] = useState<string | null>(null)

  const visibleEntries = mode === 'viewing' ? initialEntries : draft

  const hasEmptyKey = draft.some(e => e.key.trim() === '')
  const duplicateKey = useMemo(() => findDuplicateKey(draft), [draft])
  // entriesEqual assumes both lists are key-sorted. readHelmOverrides sorts its
  // output; addRow appends at the end, so a freshly-added empty row is a
  // legitimate positional diff. If a future refactor reorders draft (e.g. drag
  // to reorder), isDirty may false-positive — reshape entriesEqual then.
  const isDirty = !entriesEqual(draft, initialEntries)
  const canSave = mode === 'editing' && isDirty && !hasEmptyKey && duplicateKey === null

  function enterEdit() {
    setDraft([...initialEntries])
    setError(null)
    setMode('editing')
  }

  function cancelEdit() {
    setDraft([...initialEntries])
    setError(null)
    setMode('viewing')
  }

  function addRow() {
    setDraft(prev => [...prev, { key: '', value: '', type: 'auto' }])
  }

  function updateRow(index: number, patch: Partial<HelmOverrideEntry>) {
    setDraft(prev => prev.map((e, i) => (i === index ? { ...e, ...patch } : e)))
  }

  function deleteRow(index: number) {
    setDraft(prev => prev.filter((_, i) => i !== index))
  }

  async function save() {
    if (!canSave) return
    setMode('saving')
    setError(null)
    try {
      // Re-fetch the environment before merging so concurrent edits by another
      // admin (new sibling keys in env.Config, changes to helm_overrides we
      // didn't see) are preserved rather than clobbered by the stale snapshot
      // loaded at page mount. The draft's helm override intent still wins for
      // its own keys, but everything else in the config survives.
      const fresh = await getEnvironment(environment.id)
      const nextConfig = writeHelmOverrides(
        (fresh.config ?? {}) as Record<string, unknown>,
        draft
      )
      await updateEnvironmentConfig(environment.id, nextConfig)
      setMode('viewing')
      onUpdated()
    } catch (err) {
      // Narrow to ApiError: backend HTTP failures surface as an in-card banner
      // the user can recover from. Programmer errors (null derefs, React
      // races, merge bugs) MUST propagate to the React error boundary so they
      // are visible in dev and reported in prod — masking them as "Save
      // failed" would silently hide real bugs (review finding I-B).
      if (err instanceof ApiError) {
        setError(err.message)
        setMode('editing')
        return
      }
      throw err
    }
  }

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sliders size={16} className="text-slate-500" />
          <h3 className="text-sm font-semibold">
            {t('pages:environments.helmOverrides.title', 'Helm Overrides')}
          </h3>
        </div>
        {mode === 'viewing' && (
          <Button size="sm" variant="outline" onClick={enterEdit}>
            {t('pages:environments.helmOverrides.edit', 'Edit')}
          </Button>
        )}
      </div>

      {dropped.length > 0 && (
        <div
          className="mb-3 p-2 rounded border border-amber-200 bg-amber-50 text-xs text-amber-800 dark:bg-amber-500/10 dark:border-amber-500/20 dark:text-amber-300"
          data-testid="helm-override-dropped-banner"
        >
          {t(
            'pages:environments.helmOverrides.droppedBanner',
            '{{count}} helm override entries were dropped because their value is not a string. Contact the platform team to inspect the environment config directly.',
            { count: dropped.length }
          )}
          <ul className="mt-1 pl-4 list-disc">
            {dropped.map(d => (
              <li key={`${d.source}|${d.key}`}>
                <span className="font-mono">{d.key}</span>
                {' — '}
                <span className="font-mono">{d.actualType}</span>
                {' ('}
                <span className="font-mono">{d.source}</span>
                {')'}
              </li>
            ))}
          </ul>
        </div>
      )}

      {mode === 'viewing' && initialEntries.length === 0 && (
        <p className="text-sm text-slate-500">
          {t(
            'pages:environments.helmOverrides.empty',
            'No helm overrides configured. These durable overrides flow into every helm install, upgrade, and reinstall for this environment.'
          )}
        </p>
      )}

      {visibleEntries.length > 0 && (
        <div className="space-y-1">
          {visibleEntries.map((entry, index) => (
            <div
              key={mode === 'viewing' ? `${entry.key}|${entry.type}` : `row-${index}`}
              data-testid="helm-override-row"
              className="flex items-center gap-2 text-xs font-mono"
            >
              {mode === 'viewing' ? (
                <>
                  <span className="flex-1 truncate">{entry.key}</span>
                  <span className="flex-1 truncate text-slate-600">{entry.value}</span>
                  <Badge variant={entry.type === 'string' ? 'warning' : 'default'}>
                    {entry.type === 'string'
                      ? t('pages:environments.helmOverrides.typeString', 'string')
                      : t('pages:environments.helmOverrides.typeAuto', 'auto')}
                  </Badge>
                </>
              ) : (
                <>
                  <Input
                    placeholder={t(
                      'pages:environments.helmOverrides.keyPlaceholder',
                      'helm path (e.g. networkPolicy.additionalEgressPorts[0].port)'
                    )}
                    value={entry.key}
                    onChange={e => updateRow(index, { key: e.target.value })}
                    aria-label={t(
                      'pages:environments.helmOverrides.a11yKeyForRow',
                      'helm override key for row {{index}}',
                      { index }
                    )}
                    className="flex-1"
                  />
                  <Input
                    placeholder={t(
                      'pages:environments.helmOverrides.valuePlaceholder',
                      'value (use \\, for literal commas)'
                    )}
                    value={entry.value}
                    onChange={e => updateRow(index, { value: e.target.value })}
                    aria-label={t(
                      'pages:environments.helmOverrides.a11yValueForRow',
                      'helm override value for row {{index}}',
                      { index }
                    )}
                    className="flex-1"
                  />
                  <Select
                    value={entry.type}
                    onValueChange={v => updateRow(index, { type: v as 'auto' | 'string' })}
                  >
                    <SelectTrigger
                      aria-label={t(
                        'pages:environments.helmOverrides.a11yTypeForRow',
                        'helm override type for {{label}}',
                        { label: entry.key || `row ${index}` }
                      )}
                      className="w-24"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">
                        {t('pages:environments.helmOverrides.typeAuto', 'auto')}
                      </SelectItem>
                      <SelectItem value="string">
                        {t('pages:environments.helmOverrides.typeString', 'string')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    aria-label={t(
                      'pages:environments.helmOverrides.a11yDeleteRow',
                      'delete helm override row'
                    )}
                    onClick={() => deleteRow(index)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {mode !== 'viewing' && (
        <div className="mt-3 space-y-2">
          <Button size="sm" variant="outline" onClick={addRow}>
            <Plus size={14} className="mr-1" />
            {t('pages:environments.helmOverrides.addRow', 'Add row')}
          </Button>

          {duplicateKey && (
            <p className="text-xs text-red-500">
              {t(
                'pages:environments.helmOverrides.duplicateKey',
                'Duplicate key "{{key}}" — helm overrides must be unique across auto and string types.',
                { key: duplicateKey }
              )}
            </p>
          )}
          {error && <p className="text-xs text-red-500">{error}</p>}

          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={save}
              disabled={!canSave}
            >
              {mode === 'saving'
                ? t('pages:environments.helmOverrides.saving', 'Saving…')
                : t('pages:environments.helmOverrides.save', 'Save')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={cancelEdit}
              disabled={mode === 'saving'}
            >
              {t('pages:environments.helmOverrides.cancel', 'Cancel')}
            </Button>
          </div>
        </div>
      )}
    </Card>
  )
}
